// packages/utils/src/shared/flags/languageToCountryCode.ts
//
// Language code → the ISO 3166 code of the flag that stands in for it: a country (alpha-2), or a
// subdivision (ISO 3166-2) for a language whose flag is a region's (Catalan → 'ES-CT', the Senyera).
// The one mapping behind the web `Flag` (packages/ui) and the native one (@tourtask/mobile-ui). A
// display convention, not a fact about who speaks what. UI locales take LOCALE_CLIENT_META's `flagCountry`;
// other spoken languages are listed below. An unmapped code returns null, and callers render a blank
// slot, never a stand-in.
import type { AppLocale } from '@tourtask/types/i18n';
import { LOCALE_CLIENT_META } from '../i18n/localeClientMeta';

/** Flag country for every language that is NOT a UI locale (a UI locale here is a compile error). */
const SPOKEN_LANGUAGE_FLAG_COUNTRY: Readonly<Record<string, string>> = {
  hi: 'IN', // Hindi -> India
  bn: 'BD', // Bengali -> Bangladesh
  ko: 'KR', // Korean -> South Korea
  tr: 'TR', // Turkish -> Turkey
  vi: 'VN', // Vietnamese -> Vietnam
  te: 'IN', // Telugu -> India
  mr: 'IN', // Marathi -> India
  ta: 'IN', // Tamil -> India
  ur: 'PK', // Urdu -> Pakistan
  pl: 'PL', // Polish -> Poland
  uk: 'UA', // Ukrainian -> Ukraine
  ro: 'RO', // Romanian -> Romania
  el: 'GR', // Greek -> Greece
  he: 'IL', // Hebrew -> Israel
  sw: 'KE', // Swahili -> Kenya
  ms: 'MY', // Malay -> Malaysia
  th: 'TH', // Thai -> Thailand
  cs: 'CZ', // Czech -> Czech Republic
  fi: 'FI', // Finnish -> Finland
  no: 'NO', // Norwegian -> Norway
  da: 'DK', // Danish -> Denmark
  hu: 'HU', // Hungarian -> Hungary
  sk: 'SK', // Slovak -> Slovakia
  sr: 'RS', // Serbian -> Serbia
  hr: 'HR', // Croatian -> Croatia
  bg: 'BG', // Bulgarian -> Bulgaria
  sl: 'SI', // Slovenian -> Slovenia
  lv: 'LV', // Latvian -> Latvia
  lt: 'LT', // Lithuanian -> Lithuania
  et: 'EE', // Estonian -> Estonia
  is: 'IS', // Icelandic -> Iceland
  ka: 'GE', // Georgian -> Georgia
  af: 'ZA', // Afrikaans -> South Africa
  sq: 'AL', // Albanian -> Albania
  mk: 'MK', // Macedonian -> North Macedonia
  bs: 'BA', // Bosnian -> Bosnia and Herzegovina
  am: 'ET', // Amharic -> Ethiopia
} satisfies Readonly<Record<string, string>> & Partial<Record<AppLocale, never>>;

/** A `Map`, so a code such as 'constructor' misses instead of hitting `Object.prototype`. */
const FLAG_COUNTRY_BY_LANGUAGE: ReadonlyMap<string, string> = new Map<string, string>([
  ...Object.entries(LOCALE_CLIENT_META).map(([code, meta]): [string, string] => [code, meta.flagCountry]),
  ...Object.entries(SPOKEN_LANGUAGE_FLAG_COUNTRY),
]);

/**
 * The flag country for a language code, or `null` when we have no honest
 * answer. Accepts `'sv'`, `'SV'` and `'sv-SE'` alike — anything past the
 * first subtag is ignored, since the flag is chosen per language, not per
 * regional variant.
 */
export function languageToCountryCode(languageCode: string): string | null {
  const base = languageCode.trim().toLowerCase().split(/[-_]/)[0];
  if (base === '') return null;
  return FLAG_COUNTRY_BY_LANGUAGE.get(base) ?? null;
}
